import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useDefaultCatch } from '@atlassian/eoc-catch';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import {
	createActionsHook,
	createContainer,
	createDefaultEocWrapper,
	createHook,
	createStore,
	createSubscriber,
} from '@atlassian/eoc-rss-helpers';
import { zoned } from '@atlassian/eoc-schedule-common';

import { isEmpty } from '../../utils/validators';
import { useActions as usePageActions } from '../page-store';

import { actions } from './actions';
import { initialState } from './state';
import type { ContainerProps, WrapperProps } from './types';

const store = createStore<typeof initialState, typeof actions>({
	initialState,
	actions,
});

const useStore = createHook<typeof initialState, typeof actions>(store);

const useActions = createActionsHook<typeof initialState, typeof actions>(store);

const Container = createContainer<typeof initialState, typeof actions, ContainerProps>(store, {
	Wrapper: createDefaultEocWrapper({
		useSyncContainerProps: (): WrapperProps => {
			const flagApi = useFlags();
			const intlApi = useIntl();
			const { closePageModal } = usePageActions();
			const defaultCatch = useDefaultCatch();
			const { createAnalyticsEvent } = useAnalyticsEvents();

			return {
				flagApi,
				intlApi,
				closePageModal,
				defaultCatch,
				createAnalyticsEvent,
			};
		},
	}),
	onInit:
		() =>
		({ setState, dispatch, getState }, { schedule }) => {
			// If not initialized before
			if (getState().schedule === undefined) {
				setState({
					schedule,
					currentDate: zoned.midOfDay(new Date(), schedule.timezone),
				});

				assert(
					!isEmpty(getState().schedule),
					new AssertionError('eoc-schedule', 'schedule-store: schedule is not defined'),
				);

				dispatch(actions.getTimelineAction());
			}
		},
	onUpdate:
		() =>
		({ setState, dispatch, getState }, { schedule }) => {
			// Either timezone, name, description, enabled is changed
			// TODO: update schedule store instead of team schedule list resource
			if (!isEqual(getState().schedule, schedule) && !isEmpty(schedule)) {
				const timezone = getState().schedule.timezone;
				const utcTimeOffsetMillis = getState().schedule.utcTimeOffsetMillis;

				setState({ schedule });

				if (
					timezone !== schedule.timezone ||
					utcTimeOffsetMillis !== schedule.utcTimeOffsetMillis
				) {
					dispatch(actions.getTimelineAction());
				}
			}
		},
});

const Subscriber = createSubscriber(store);

export { Container, useStore, useActions, store, Subscriber };
